import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

const Rmu = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingDocs, setIsLoadingDocs] = useState(false);
    const [message, setMessage] = useState(null);

    const fileInputRef = useRef(null);

    const showMessage = (text, type) => {
        setMessage({ text, type });

        setTimeout(() => {
            setMessage(null);
        }, 4000);
    };

    const fetchDocuments = async () => {
        setIsLoadingDocs(true);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/rmu/rmu-file`
            );

            setDocuments(response.data.data || []);
        } catch (error) {
            console.error("Fetch Error:", error);

            showMessage(
                error?.response?.data?.message ||
                    "Failed to fetch documents",
                "error"
            );
        } finally {
            setIsLoadingDocs(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, []);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            showMessage("Please select a file", "error");
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);

        setIsLoading(true);

        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/rmu/rmu-file`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            showMessage(
                "File uploaded successfully",
                "success"
            );

            setSelectedFile(null);

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }

            fetchDocuments();
        } catch (error) {
            console.error("Upload Error:", error);

            showMessage(
                error?.response?.data?.message ||
                    "Upload failed",
                "error"
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = async (originalName) => {
        try {
            const textToCopy = `${process.env.REACT_APP_API_URL}/rmu/rmu-file/download/${originalName}`;

            await navigator.clipboard.writeText(textToCopy);

            showMessage("Path copied successfully", "success");
        } catch (error) {
            console.error("Copy failed", error);
        }
    };

    const handleDownload = async (originalName) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/rmu/rmu-file/download/${originalName}`,
                {
                    responseType: "blob",
                }
            );

            const url = window.URL.createObjectURL(
                new Blob([response.data])
            );

            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", originalName);

            document.body.appendChild(link);

            link.click();

            link.remove();

            window.URL.revokeObjectURL(url);

            showMessage(
                "File downloaded successfully",
                "success"
            );
        } catch (error) {
            console.error("Download Error:", error);

            showMessage(
                error?.response?.data?.message ||
                    "Download failed",
                "error"
            );
        }
    };

    return (
        <div className="min-h-screen bg-[#f4f7fb] p-6">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* HEADER */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">
                            File Management System
                        </h1>

                        <p className="text-gray-500 mt-1">
                            Upload, manage and download your documents
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-2xl px-6 py-4 shadow-sm min-w-[180px]">
                        <p className="text-sm text-gray-500">
                            Total Documents
                        </p>

                        <h2 className="text-3xl font-bold text-blue-600 mt-1">
                            {documents.length}
                        </h2>
                    </div>
                </div>

                {/* MESSAGE */}
                {message && (
                    <div
                        className={`px-5 py-4 rounded-2xl text-sm font-medium border shadow-sm ${
                            message.type === "success"
                                ? "bg-green-50 border-green-200 text-green-700"
                                : "bg-red-50 border-red-200 text-red-700"
                        }`}
                    >
                        {message.text}
                    </div>
                )}

                {/* UPLOAD SECTION */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex flex-col xl:flex-row gap-5 xl:items-center">

                        {/* FILE AREA */}
                        <div className="flex-1">
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={handleFileChange}
                                className="hidden"
                                id="fileUpload"
                            />

                            <label
                                htmlFor="fileUpload"
                                className="border-2 border-dashed border-gray-300 hover:border-blue-500 bg-gray-50 
                                hover:bg-blue-50 transition-all duration-300 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mb-4">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-8 h-8 text-blue-600"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                        />
                                    </svg>
                                </div>

                                <p className="text-lg font-semibold text-gray-700">
                                    Choose File
                                </p>

                                {selectedFile && (
                                    <div className="mt-5 bg-white border border-gray-200 rounded-xl px-4 py-3 w-full max-w-lg shadow-sm">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                                📄
                                            </div>

                                            <div className="flex-1 overflow-hidden">
                                                <p className="font-medium text-gray-800 truncate">
                                                    {selectedFile.name}
                                                </p>

                                                <p className="text-sm text-gray-500">
                                                    {(
                                                        selectedFile.size /
                                                        1024
                                                    ).toFixed(2)}{" "}
                                                    KB
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </label>
                        </div>

                        {/* UPLOAD BUTTON */}
                        <div className="w-full xl:w-auto">
                            <button
                                onClick={handleUpload}
                                disabled={isLoading}
                                className="w-full xl:w-auto h-[58px] bg-blue-600 hover:bg-blue-700 text-white px-8 rounded-2xl font-semibold shadow-sm transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3"
                            >
                                {isLoading ? (
                                    <>
                                        <svg
                                            className="animate-spin h-5 w-5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>

                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v8z"
                                            ></path>
                                        </svg>

                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M12 4v16m8-8H4"
                                            />
                                        </svg>

                                        Upload File
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* TABLE SECTION */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">

                    {/* TABLE HEADER */}
                    <div className="flex items-center justify-between px-6 py-5 border-b bg-[#fafbfc]">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">
                                Uploaded Documents
                            </h2>

                            <p className="text-sm text-gray-500 mt-1">
                                View and manage uploaded files
                            </p>
                        </div>

                        {isLoadingDocs && (
                            <div className="text-sm text-blue-600 font-medium animate-pulse">
                                Loading...
                            </div>
                        )}
                    </div>

                    {/* EMPTY STATE */}
                    {!isLoadingDocs &&
                        documents.length === 0 && (
                            <div className="py-20 text-center">
                                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5 text-4xl">
                                    📂
                                </div>

                                <h3 className="text-xl font-semibold text-gray-700">
                                    No Documents Available
                                </h3>

                                <p className="text-gray-500 mt-2">
                                    Upload your first document to get started
                                </p>
                            </div>
                        )}

                    {/* TABLE */}
                    {documents.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[850px]">

                                {/* TABLE HEAD */}
                                <thead className="bg-[#f9fafb] border-b">
                                    <tr>
                                        <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                            File Name
                                        </th>

                                        <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                            File Type
                                        </th>

                                        <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                            Created Date
                                        </th>

                                        <th className="text-center px-6 py-4 text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>

                                {/* TABLE BODY */}
                                <tbody>
                                    {documents.map((doc, index) => (
                                        <tr
                                            key={doc._id}
                                            className={`border-b hover:bg-gray-50 transition duration-200 ${
                                                index % 2 === 0
                                                    ? "bg-white"
                                                    : "bg-[#fcfcfc]"
                                            }`}
                                        >
                                            {/* FILE NAME */}
                                            <td className="px-6 py-5">
                                                <div className="flex items-center gap-4">

                                                    <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center">
                                                        📄
                                                    </div>

                                                    <div className="max-w-[350px]">
                                                        <p className="font-semibold text-gray-800 truncate">
                                                            {doc.originalName}
                                                        </p>

                                                        <p className="text-sm text-gray-500">
                                                            Document File
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* FILE TYPE */}
                                            <td className="px-6 py-5">
                                                <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold border border-blue-100">
                                                    {doc.originalName
                                                        ?.split(".")
                                                        ?.pop()
                                                        ?.toUpperCase()}
                                                </span>
                                            </td>

                                            {/* DATE */}
                                            <td className="px-6 py-5">
                                                <div>
                                                    <p className="font-medium text-gray-700">
                                                        {new Date(
                                                            doc.createdAt
                                                        ).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </td>

                                            {/* ACTIONS */}
                                            <td className="px-6 py-5">
                                                <div className="flex items-center justify-center gap-3">

                                                    {/* DOWNLOAD */}
                                                    <button
                                                        onClick={() =>
                                                            handleDownload(
                                                                doc.originalName
                                                            )
                                                        }
                                                        className="group w-11 h-11 rounded-xl bg-blue-50 hover:bg-blue-600 border border-blue-100 hover:border-blue-600 flex items-center justify-center transition-all duration-300"
                                                        title="Download File"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="w-5 h-5 text-blue-600 group-hover:text-white"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                            strokeWidth={2}
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1"
                                                            />

                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M12 4v12m0 0l-4-4m4 4l4-4"
                                                            />
                                                        </svg>
                                                    </button>

                                                    {/* COPY */}
                                                    <button
                                                        onClick={() =>
                                                            handleCopy(
                                                                doc.originalName
                                                            )
                                                        }
                                                        className="group w-11 h-11 rounded-xl bg-gray-100 hover:bg-gray-800 border border-gray-200 hover:border-gray-800 flex items-center justify-center transition-all duration-300"
                                                        title="Copy Path"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="w-5 h-5 text-gray-700 group-hover:text-white"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                            strokeWidth={2}
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2"
                                                            />

                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M10 8h8a2 2 0 012 2v8a2 2 0 01-2 2h-8a2 2 0 01-2-2v-8a2 2 0 012-2z"
                                                            />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Rmu;